import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, AlertTriangle, Clock3 } from "lucide-react";
import { toast } from "react-toastify";
import SearchBar from "../../components/SearchBar";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";
import { formatDateTime } from "../../utils/format";
import { getUserRole } from "../../utils/getUserRole";

const WEEKDAYS = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

const formatScheduleTime = (time) => {
  const [hour, minute] = time.split(":").map(Number);
  const period = hour < 12 ? "a. m." : "p. m.";
  return `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${period}`;
};

function Requests() {
  const navigate = useNavigate();
  const isAdmin = getUserRole() === "Administrador";
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const ITEMS_PER_PAGE = 30;
  const [currentPage, setCurrentPage] = useState(0);
  const [showClosedModal, setShowClosedModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [requestSchedule, setRequestSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const res = await api.get("/inventory-requests");
      setRequests(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = requests.filter((request) =>
    `${request.request_number} ${request.client?.name || ""}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const pageCount = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);

  const currentItems = filteredRequests.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const handleNewRequest = () => {
    api
      .get("/inventory-requests/schedule")
      .then((res) => {
        setRequestSchedule(res.data);
        if (res.data.is_open) {
          navigate("/requests/create");
        } else {
          setShowClosedModal(true);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("No se pudo consultar el horario de solicitudes");
      });
  };

  const handleOpenScheduleModal = async () => {
    try {
      const res = await api.get("/inventory-requests/schedule");
      const schedule = res.data;
      setRequestSchedule(schedule);
      setScheduleForm({
        start_day: schedule.start_day,
        start_time: schedule.start_time,
        end_day: schedule.end_day,
        end_time: schedule.end_time,
      });
      setShowScheduleModal(true);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo cargar la configuración del horario");
    }
  };

  const handleSaveSchedule = async (event) => {
    event.preventDefault();
    setSavingSchedule(true);

    try {
      const res = await api.put("/inventory-requests/schedule", {
        ...scheduleForm,
        start_day: Number(scheduleForm.start_day),
        end_day: Number(scheduleForm.end_day),
      });
      setShowScheduleModal(false);
      toast.success(res.data.message);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "No se pudo guardar el horario");
    } finally {
      setSavingSchedule(false);
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-semibold">
            Pendiente
          </span>
        );

      case "delivered":
        return (
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
            Entregada
          </span>
        );

      case "cancelled":
        return (
          <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
            Cancelada
          </span>
        );

      default:
        return status;
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <h2 className="text-3xl font-semibold text-gray-800">
          Solicitudes de Pedido
        </h2>

        <div className="flex items-center gap-3">
          <div className="w-64">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleNewRequest}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition duration-300"
            >
              <Plus size={18} />
              Nueva Solicitud
            </button>
            {isAdmin && (
              <button
                onClick={handleOpenScheduleModal}
                className="bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition duration-300"
              >
                <Clock3 size={18} />
                Configurar horario
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Número</th>

              <th className="px-6 py-3">Área</th>

              <th className="px-6 py-3">Usuario</th>

              <th className="px-6 py-3">Fecha</th>

              <th className="px-6 py-3">Estado</th>

              <th className="px-6 py-3 text-center w-28">Acciones</th>
            </tr>
          </thead>

          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  className="px-6 py-6 text-center text-gray-400 italic"
                >
                  No hay solicitudes.
                </td>
              </tr>
            ) : (
              currentItems.map((request, index) => (
                <tr
                  key={request.id}
                  className={`border-t ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="px-6 py-4 font-medium">
                    {request.request_number}
                  </td>

                  <td className="px-6 py-4">{request.client?.name}</td>

                  <td className="px-6 py-4">{request.user?.name}</td>

                  <td className="px-6 py-4">
                    {formatDateTime(request.requested_at)}
                  </td>

                  <td className="px-6 py-4">{getStatus(request.status)}</td>

                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      <Link
                        to={`/requests/${request.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg shadow"
                        title="Ver detalle"
                      >
                        <Eye size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {showClosedModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
              <div className="flex justify-center mb-5">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle size={42} className="text-red-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-red-600 text-center">
                Solicitudes cerradas
              </h3>

              <p className="mt-5 text-gray-700 text-center leading-7">
                Las solicitudes de insumos únicamente pueden realizarse desde el
                <br />
                <strong>
                  {requestSchedule
                    ? `${WEEKDAYS[requestSchedule.start_day]} ${formatScheduleTime(requestSchedule.start_time)}`
                    : "el inicio del horario configurado"}
                </strong>
                <br />
                hasta el
                <br />
                <strong>
                  {requestSchedule
                    ? `${WEEKDAYS[requestSchedule.end_day]} ${formatScheduleTime(requestSchedule.end_time)}`
                    : "el cierre del horario configurado"}
                </strong>
              </p>

              <p className="mt-5 text-gray-500 text-center">
                Si requiere un insumo fuera de este horario, comuníquese con el
                área de insumos para validar el procedimiento correspondiente.
              </p>

              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setShowClosedModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-semibold transition"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        )}

        {showScheduleModal && scheduleForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <form
              onSubmit={handleSaveSchedule}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full"
            >
              <h3 className="text-2xl font-bold text-gray-800 text-center">
                Horario para solicitudes
              </h3>
              <p className="mt-2 mb-6 text-gray-500 text-center">
                Configura el día y la hora de apertura y cierre semanal.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <label className="text-sm font-medium text-gray-700">
                  Día de apertura
                  <select
                    value={scheduleForm.start_day}
                    onChange={(event) =>
                      setScheduleForm({
                        ...scheduleForm,
                        start_day: event.target.value,
                      })
                    }
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {WEEKDAYS.map((day, index) => (
                      <option key={day} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Hora de apertura
                  <input
                    type="time"
                    required
                    value={scheduleForm.start_time}
                    onChange={(event) =>
                      setScheduleForm({
                        ...scheduleForm,
                        start_time: event.target.value,
                      })
                    }
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Día de cierre
                  <select
                    value={scheduleForm.end_day}
                    onChange={(event) =>
                      setScheduleForm({
                        ...scheduleForm,
                        end_day: event.target.value,
                      })
                    }
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {WEEKDAYS.map((day, index) => (
                      <option key={day} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-medium text-gray-700">
                  Hora de cierre
                  <input
                    type="time"
                    required
                    value={scheduleForm.end_time}
                    onChange={(event) =>
                      setScheduleForm({
                        ...scheduleForm,
                        end_time: event.target.value,
                      })
                    }
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </label>
              </div>

              <p className="mt-4 text-xs text-gray-500">
                El periodo se repite cada semana. La hora de cierre está incluida.
              </p>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  disabled={savingSchedule}
                  className="border border-gray-300 hover:bg-gray-50 px-5 py-2 rounded-lg font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingSchedule}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-lg font-semibold"
                >
                  {savingSchedule ? "Guardando..." : "Guardar horario"}
                </button>
              </div>
            </form>
          </div>
        )}

        <Pagination
          pageCount={pageCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          currentItemsCount={currentItems.length}
          totalItemsCount={filteredRequests.length}
        />
      </div>
    </div>
  );
}

export default Requests;
